import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { IUser } from '@shared/models/user';
import { ProfileService } from '@core/services/profile.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser } from '@state/auth/auth.selectors';
import { mergeMap, of, take, throwError } from 'rxjs';
import { IGraphQLErrors } from '@shared/models/graphql';

@Component({
  selector: 'app-verify-identity-email-update',
  templateUrl: './email-update.component.html',
  styleUrls: ['./email-update.component.scss'],
})
export class VerifyIdentityEmailUpdateComponent implements OnInit {
  @Input({ required: true }) currentEmail: string = '';
  @Output() closeEmailUpdate = new EventEmitter<void>();

  protected loading = false;
  protected focused = false;
  protected user: IUser | undefined;

  protected updateEmailForm: FormGroup = this.formBuilder.group({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private profileService: ProfileService,
    private toastr: ToastrService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectAuthUser)
      .pipe(take(1))
      .subscribe((user) => {
        this.user = user;
      });
  }

  submitHandler() {
    if (this.updateEmailForm.invalid) {
      this.updateEmailForm.markAllAsTouched();
      return;
    }

    if (!this.user) {
      this.toastr.error(
        'Unable to update email. Please try again later or contact support.',
        'Error'
      );
      return;
    }
    this.loading = true;

    this.profileService
      .updateProfile({
        input: {
          firstName: this.user.firstName,
          surname: this.user.surname,
          email: this.updateEmailForm.value.email,
          emailConfirmation: this.updateEmailForm.value.email,
        },
      })
      .pipe(
        // check response before subscribe
        mergeMap((res) => {
          if (res.updateProfile.errors && res.updateProfile.errors.length) {
            return throwError(() => ({ errors: res.updateProfile.errors }));
          }
          return of(res);
        })
      )
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.toastr.success('Email updated successfully', 'Success');
          this.closeEmailUpdate.emit();
        },
        error: (err: IGraphQLErrors) => {
          this.updateEmailForm.markAllAsTouched();
          this.loading = false;

          if (err?.errors && err.errors?.length) {
            err.errors.forEach((error) => {
              if (error?.message === 'has already been taken') {
                this.updateEmailForm.controls['email'].setErrors({
                  server: 'Email has already been taken',
                });
              }
            });
          }

          this.toastr.error(
            'Failed to update email. Please try again or contact support.',
            'Error'
          );
        },
      });
  }
}
